import React, { useEffect } from "react";
import { PrivacyPolicyBody } from "@/components/PrivacyPolicyBody";
import { getBundle, PRIVACY_POLICY_VERSION } from "@/lib/signupI18n";

/*
 * Modal that displays the full Privacy Policy in the currently selected
 * locale. Opened from the account-creation screen. Closing the modal does
 * NOT change consent state — the checkbox on the signup form is the only
 * place the user agrees. The modal only fulfils the "user can read the
 * full policy before consenting" requirement.
 */

export default function PrivacyPolicyModal({ locale, onClose }) {
  const b = getBundle(locale);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pp-modal-title"
      data-testid="privacy-policy-modal"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl max-h-[92vh] flex flex-col rounded-3xl bg-white shadow-2xl border border-[color:var(--border-stone)]">
        <div className="px-6 sm:px-8 pt-5 pb-3 border-b border-[color:var(--border-stone)] flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand)]">
              English Job Interview Simulator
            </div>
            <h2
              id="pp-modal-title"
              className="text-xl sm:text-2xl font-semibold tracking-tight text-[color:var(--text-primary)]"
            >
              {b.privacySectionTitle}
            </h2>
            <div className="mt-1 text-[11px] text-[color:var(--text-secondary)]">
              {b.policyVersionLabel}: <span className="font-mono">{PRIVACY_POLICY_VERSION}</span> · {locale}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[color:var(--border-stone)] px-3 py-1.5 text-xs hover:bg-[color:var(--bg-surface-hover)]"
            data-testid="privacy-policy-close"
          >
            {b.privacyClose}
          </button>
        </div>
        <div
          className="px-6 sm:px-8 py-4 overflow-y-auto flex-1 min-h-0"
          data-testid="privacy-policy-modal-body"
        >
          <PrivacyPolicyBody locale={locale} />
        </div>
      </div>
    </div>
  );
}
